<template>
  <div class="birds-eye-widget" v-show="sidebarOpen">
    <div class="toggle">
      <div class="show-sirah-label">{{ $t('showIbnIshaq') }}</div>
      <div class="show-sirah-toggle">
        <input class="tgl tgl-flat" id="show-sirah" type="checkbox" v-model="showSirah" />
        <label class="tgl-btn" for="show-sirah"></label>
      </div>
    </div>
    <div class="toggle">
      <div class="show-text-reuse-label">{{ $t('showTextReuse') }}</div>
      <div class="show-text-reuse-toggle">
        <input class="tgl tgl-flat" id="show-text-reuse" type="checkbox" v-model="showTextReuse" />
        <label class="tgl-btn" for="show-text-reuse"></label>
      </div>
    </div>

    <ReaderSelector
      v-if="readerOptions.length > 0"
      v-model="readerId"
      :current-reader="readerId"
      :current-title="currentTitle"
      :options="readerOptions"
    />

    <LoaderBall v-if="selectedWork && loading" />
    <SectionBirdsEye
        v-else-if="selectedWork"
        :superheadings="superheadings"
        :readerId="readerId"
        :showSirah="showSirah"
        :showTextReuse="showTextReuse"
        @select="onSelect"
    />
  </div>
</template>

<script>
  import i18n from '../i18n';
  import LoaderBall from '../components/LoaderBall.vue';
  import SectionBirdsEye from '../components/SectionBirdsEye.vue';
  import ReaderSelector from '../components/ReaderSelector/ReaderSelector.vue';
  import mixins from '../mixins';
  import utils from '../utils';
  import { TOGGLE_SHOW_SIRAH } from '../constants';

  export default {
    scaifeConfig: {
      displayName: () => i18n.t('sectionBirdsEyeWidgetTitle'),
      singleton: true,
      location: 'sidebar',
      defaults: {
        open: true,
        fixed: true,
      },
    },
    mixins: [mixins.RouteReaderSelectionsMixin, mixins.LocalizedWorkTitleMixin],
    components: { SectionBirdsEye, LoaderBall, ReaderSelector },
    data() {
      return {
        sira: null,
        loading: false,
        readerId: null,
        showTextReuse: false,
      };
    },
    watch: {
      selectedWork: {
        immediate: true,
        handler() {
          if (this.selectedWork) {
            this.fetchData();
          }
        },
      },
      threshold: {
        handler() {
          if (this.selectedWork) {
            this.fetchData();
          }
        },
      },
      readerOptions: {
        handler() {
          const hasOptions = this.readerOptions.length;
          const noReaderSelected = !this.reader || !this.readerId;
          if (hasOptions && noReaderSelected) {
            this.readerId = this.readerOptions[0].readerId;
          }
        },
      },
    },
    computed: {
      sidebarOpen() {
        // avoid a race condition setting the inital readerId
        return this.$store.state.scaifeSkeleton.leftOpen;
      },
      reader() {
        return this.readerId ? this.$store.getters.readerById(this.readerId).reader : null;
      },
      readers() {
        return this.$store.state.readers;
      },
      currentTitle() {
        return this.selectedWork ? this.localizedWorkTitle({ urn: this.selectedWork }) : '';
      },
      readerOptions() {
        return this.readers.filter(reader => reader.selection.workUrn).map(reader => ({
          readerId: reader.readerId,
          title: this.localizedWorkTitle({ urn: reader.selection.workUrn }),
        }));
      },
      selectedWork() {
        return this.reader && this.reader.selection.workUrn ? this.reader.selection.workUrn : null;
      },
      superheadings() {
        if (this.sira === null) {
          return [];
        }
        return this.sira.superheadings.edges.map((superheading) => {
          const sections = superheading.node.sections.edges.map(section => ({
            highlight: section.node.isSirah && this.showSirah,
            highlightTextReuse: section.node.firstAlignmentIdx !== null && this.showTextReuse,
            sectionId: section.node.id,
            superheadingId: superheading.node.id,
            sectionIdx: section.node.idx,
            firstSirahLineIdx: section.node.firstSirahLineIdx,
            firstAlignmentIdx: section.node.firstAlignmentIdx,
            depth: section.node.depth,
            label: section.node.label,
            textContent: section.node.textContent,
            readerVisible: this.isVisible(section.node.idx),
          }));
          return {
            label: superheading.node.label,
            sections,
          };
        });
      },
      threshold() {
        return this.$store.state.textReuseMatchThreshold;
      },
      visibleSections() {
        return this.$store.state.readerVisibleSections;
      },
      showSirah: {
        get() {
          return this.$store.state.showSirah;
        },
        set() {
          this.$store.dispatch(TOGGLE_SHOW_SIRAH);
        },
      },
    },
    methods: {
      onClick(readerId) {
        this.readerId = readerId;
      },
      onSelect(section, lineIdx) {
        const id = utils.makeId(this.selectedWork, section.sectionIdx, lineIdx);
        const selection = utils.parseId(id);
        const { readerId } = this;
        this.updateReader(readerId, selection);
      },
      fetchData() {
        const query = `{
          superheadings(work_Urn: "${this.selectedWork}") {
            edges {
              node {
                id
                position
                label
                sections {
                  edges {
                    node {
                      id
                      depth
                      idx
                      isSirah
                      firstSirahLineIdx
                      firstAlignmentIdx(threshold_Gte: ${this.threshold})
                      label
                      textContent
                    }
                  }
                }
              }
            }
          }
        }`;
        this.loading = true;
        this.$gql(query).then((data) => {
          this.sira = data;
          this.loading = false;
        });
      },
      isVisible(sectionIdx) {
        return this.visibleSections[this.readerId] === sectionIdx;
      },
    },
  };
</script>

<style lang="scss">
  @import "../variables.scss";
  #app[data-locale="ar"] .birds-eye-widget > div {
      &.toggle {
        direction: rtl;
        font-family: $ar-font-family;
        font-size: $ar-default-font-size;
        padding-left: 0;
        padding-right: 8px;
        > div {
          margin-right: 0;
          margin-left: 10px;
        }
      }
  }
  .birds-eye-widget > div {
      width: 50%;
      margin: 20px auto;
      &.toggle {
        display: flex;
        justify-content: flex-start;
        padding-left: 8px;
        > div {
          margin-top: auto;
          margin-bottom: auto;
          margin-right: 10px;
        }
        width: 100%;
        margin: 20px 0 20px 0;
      }
      .state-button.populated {
        cursor: pointer;
      }
  }
</style>
